import React, { useCallback, useState, useEffect } from 'react';
import './TypePickerDialog.less';
import { IEntityPickerTreeModel } from 'components/EntityPicker/defaultEpickTreeModel';
import { entityPickerFetcher } from 'components/EntityPicker/entityPickerFetcher';
import { globalSearchEntityPickerTreeModel } from 'components/EntityPicker/globalSearchEntityPickerTreeModel';
import { EpickTree, IEpickNode } from 'components/EntityPicker/EpickTree';
import { decisionsTypeTreeModel } from 'components/EntityPicker/decisionsTypeTreeModel';
import { SearchField } from 'components/inputs/SearchField';
import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import { Dialog } from 'components/Dialogs/Dialog';
import { DecisionsType } from '@decisions/server/DecisionsFramework/Design/Flow/Mapping';
import { DecisionsTypePickerSettings } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/DecisionsTypePickerSettings';
import { getDecisionsTypeById } from 'utils/DecisionsTypeUtils';

interface ITypePickerDialog {
  open: boolean;
  setClosed: () => void;
  settings: DecisionsTypePickerSettings;
  onSelected: (type: DecisionsType) => void;
}

/**
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function TypePickerDialog({ open, settings, setClosed, onSelected }: ITypePickerDialog) {
  const [searchText, setSearchText] = useState<string>('');
  const [selectedType, setSelectedType] = useState<DecisionsType | null>();
  const [model, setModel] = useState<IEntityPickerTreeModel | null>();
  const [hasNodes, setHasNodes] = useState<boolean>(false);

  const onClear = useCallback((resetType: boolean = true) => {
    setSearchText('');
    setModel(null);
    resetType && setSelectedType(null);
  }, []);

  const updateModel = useCallback(async () => {
    const dataloader = entityPickerFetcher({
      filterNames: settings.FilterNames,
      types: settings.Types,
    });
    const model = searchText
      ? globalSearchEntityPickerTreeModel(searchText, settings, dataloader)
      : decisionsTypeTreeModel(settings, dataloader);
    setModel(model);
  }, [searchText, settings]);

  const handleNodeSelected = useCallback(async (item: IEpickNode) => {
    if (!item?.pickable) {
      return;
    }
    const type: DecisionsType = await getDecisionsTypeById(item.id);
    if (type) {
      setSelectedType(type);
    }
  }, []);

  const handleNodePick = () => {
    if (selectedType) {
      onSelected(selectedType);
      setClosed();
    }
  };

  useEffect(() => {
    if (open) {
      updateModel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, open]);

  useEffect(() => {
    return onClear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onModelLoaded = (model?: ITreeNode<IEpickNode>[]) => {
    setHasNodes(!!(model && model.length));
  };

  return (
    <Dialog
      dialogTitle={{ titleText: 'Pick Type' }}
      styles={{ height: 700, width: 400 }}
      open={open}
      dialogActions={{
        actions: [
          {
            name: 'Pick',
            onClick: handleNodePick,
            isDisabled: !selectedType,
          },
        ],
      }}
      onClose={setClosed}
      className="dp-type-picker-editor-dialog"
    >
      <div className="dp-type-picker-editor-dialog__search-box">
        <SearchField
          value={searchText}
          onSearch={setSearchText}
          onClear={() => onClear(false)}
          className="dp-type-picker-editor-dialog__search-input"
        />
      </div>
      <div className="dp-type-picker-editor-dialog__content">
        {model && (
          <>
            <EpickTree model={model} onNodeSelected={handleNodeSelected} onTreeLoaded={onModelLoaded} />
            {!hasNodes && !!searchText.length && (
              <div className="dp-type-picker-editor-dialog__message-box">
                <div className="dp-type-picker-editor-dialog__message-box-empty-result">No Search Results</div>
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
